import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Form, Input, Select, Switch } from "antd";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/services/column";
import ModalComponent from "../../components/modals/ModalComponent";
import LoadableButton from "../../components/button/LoadableButton";
import View from "../../assets/View";
import Add from "../../assets/Add";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { getAllPipelineApi } from "../../api/request/pipeline";
import {
  CreateServiceApi,
  DeleteServiceApi,
  getAllServiceApi,
  UpdateServiceApi,
  UpdateServiceStatusApi,
} from "../../api/request/service";
import Edit from "../../assets/Edit";
import GlobalTooltip from "../../components/common/GlobalTooltip";

const { Option } = Select;

const Services = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pipelineData, setPipelineData] = useState([]);
  const [mode, setMode] = useState("add");
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [selectedService, setSelectedService] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const handleView = (data) => {
    navigate(`/service_template/${data?.id}`, {
      state: { service: data?.name },
    });
  };

  const handleStatusChange = async (checked, selectedService) => {
    try {
      const newStatus = checked ? "active" : "inactive";
      const { data } = await UpdateServiceStatusApi({
        id: selectedService?.id,
        status: newStatus,
      });

      if (data.status) {
        toast.success(data?.message);
        getServiceList();
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const getServiceList = async () => {
    try {
      setIsLoading(true);
      const { data } = await getAllServiceApi();
      if (data?.status) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              serviceName: <p className="capitalize">{item?.name}</p>,
              status: (
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={item?.status === "active"}
                    onChange={(checked) => handleStatusChange(checked, item)}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                </div>
              ),
              actions: (
                <div
                  className="flex gap-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GlobalTooltip title={"Manage Services"}>
                    <button onClick={() => handleView(item)}>
                      <View />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title={"Edit Service"}>
                    <button
                      onClick={() => {
                        setSelectedService(item);
                        setMode("edit");
                        setIsOpen(true);
                      }}
                    >
                      <Edit />
                    </button>
                  </GlobalTooltip>
                  <GlobalTooltip title={"Delete Service"}>
                    <button
                      onClick={() => {
                        setSelectedService(item);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </GlobalTooltip>
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_record);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  const getPipelineList = async () => {
    try {
      const { data } = await getAllPipelineApi();
      if (data.status) setPipelineData(data?.data);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    getServiceList();
    getPipelineList();
  }, []);

  useEffect(() => {
    if (mode === "edit" && selectedService) {
      form.setFieldsValue({
        service_name: selectedService?.name,
        pipeline_temp_id: selectedService?.pipeline_temp_id,
        service_number_title: selectedService?.service_number_title,
        service_document_title: selectedService?.service_document_title,
        is_auto_fill: selectedService?.is_auto_fill,
        is_bookkeeping: selectedService?.is_bookkeeping,
        description: selectedService?.description,
      });
    } else {
      form.resetFields();
    }
  }, [mode, selectedService, isOpen]);

  const addUpdateService = (value) => {
    if (mode === "add") createService(value);
    else updateService(value);
  };

  const createService = async (value) => {
    try {
      const params = {
        ...value,
        is_book_keeping: value?.is_book_keeping === true ? 1 : 0,
      };
      setIsCreateLoading(true);
      const { data } = await CreateServiceApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsOpen(false);
        getServiceList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsCreateLoading(false);
    }
  };

  const updateService = async (value) => {
    if (!selectedService?.id) return;
    try {
      setIsCreateLoading(true);
      const params = {
        service_type_id: selectedService?.id,
        ...value,
        is_bookkeeping: value?.is_bookkeeping === true ? 1 : 0,
      };
      const { data } = await UpdateServiceApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsOpen(false);
        setSelectedService({});
        getServiceList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsCreateLoading(false);
    }
  };

  const deleteService = async () => {
    if (!selectedService?.id) return;
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteServiceApi({
        service_type_id: selectedService?.id,
      });
      if (data?.status) {
        toast.success(data?.message);
        setIsDeleteModalOpen(false);
        setSelectedService({});
        getServiceList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const updatedColumn = [
    ...columns,
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 100,
    },
  ];

  useEffect(() => {
    if (!isOpen) form.resetFields();
  }, [isOpen]);

  return (
    <div>
      <div className="border bg-White rounded-xl pb-5">
        <div className="flex items-center justify-between p-5">
          <h2 className="title">Services {total ? `(${total})` : null}</h2>
          <div>
            <button
              className="btn-primary !px-4 flex items-center gap-2"
              onClick={() => {
                setMode("add");
                setIsOpen(true);
              }}
            >
              <Add />
              <span>Add Service</span>
            </button>
          </div>
        </div>

        <SkeletonTable
          columns={updatedColumn}
          searchingFields={["name", "pipeline_temp_name"]}
          data={data}
          isLoading={isLoading}
          onRowClick={(data) => handleView(data)}
        />
      </div>

      <ModalComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={`${mode === "add" ? "Create" : "Update"} Service`}
        width={600}
      >
        <Form
          autoComplete="off"
          layout="vertical"
          form={form}
          onFinish={addUpdateService}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <Form.Item
              name="service_name"
              label="Service Name"
              rules={[
                {
                  required: true,
                  message: "Please enter service name!",
                },
              ]}
            >
              <Input placeholder="Enter service name..." size="large" />
            </Form.Item>

            <Form.Item
              name="pipeline_temp_id"
              label="Service Flow"
              // className="mb-2"
              rules={[
                { required: true, message: "Please select a service flow!" },
              ]}
            >
              <Select placeholder="Select service flow" size="large">
                {pipelineData?.map((item) => (
                  <Option value={item?.id}>{item?.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="service_number_title"
              label="Service Number Title"
              rules={[
                {
                  required: true,
                  message: "Please enter a service number title!",
                },
              ]}
            >
              <Input placeholder="Enter Service Number Title..." size="large" />
            </Form.Item>

            <Form.Item
              label="Service Document Title"
              name="service_document_title"
              rules={[
                {
                  required: true,
                  message: "Please enter a service document title!",
                },
              ]}
            >
              <Input
                placeholder="Enter Service Document Title..."
                size="large"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Please enter a description!" },
              {
                min: 50,
                message: "Description must be at least 50 characters.",
              },
              {
                max: 80,
                message: "Description cannot exceed 70 characters.",
              },
              {
                validator: (_, value) =>
                  value && value.length >= 50 && value.length <= 80
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "Description must be between 50 and 80 characters."
                        )
                      ),
              },
            ]}
          >
            <Input.TextArea
              placeholder="Please enter between 50 to 80 characters."
              size="large"
              autoSize={{ minRows: 3, maxRows: 5 }}
              maxLength={80}
              showCount
            />
          </Form.Item>

          <div className="grid sm:grid-cols-2 mb-5">
            <div className="flex items-center gap-5">
              <p>Enable Auto File Fill</p>
              <Form.Item name="is_auto_fill" className="!m-0">
                <Switch size="large" />
              </Form.Item>
            </div>

            <div className="flex items-center gap-5">
              <p>Enable BookKeeping</p>
              <Form.Item name="is_bookkeeping" className="!m-0">
                <Switch size="large" />
              </Form.Item>
            </div>
          </div>

          <div className="text-center mt-5">
            <LoadableButton
              type="submit"
              lable="Submit"
              loadingLable={`${
                mode === "add" ? "Creating" : "Updating"
              } Service...`}
              isLoading={isCreateLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      {/* --- Delete confirmation modal --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this service?`}
        onConfirm={deleteService}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting service...`}
      />
    </div>
  );
};

export default Services;
